<p-confirmDialog></p-confirmDialog>
<p-toast></p-toast>

<div class="tm-page">

  <!-- Page Header -->
  <div class="tm-header">
    <h1 class="tm-title">Task Management</h1>
    <div class="switch-tabs">
      <button [class.active]="activeTab === 'statuses'" (click)="activeTab = 'statuses'">Statuses</button>
      <button [class.active]="activeTab === 'attributes'" (click)="activeTab = 'attributes'">Attributes</button>
      <button [class.active]="activeTab === 'tags'" (click)="activeTab = 'tags'">Tags</button>
    </div>
  </div>

  <!-- ===== Statuses Tab ===== -->
  @if (activeTab === 'statuses') {
    <div class="tm-panel">
      <div class="tm-panel-hdr">
        <p class="tm-panel-desc">Manage your task workflow with custom statuses.</p>
        <button class="tm-btn-add" type="button" (click)="openCreateStatusDialog()">
          <i class="ri-add-line"></i> New Status
        </button>
      </div>

      <div class="tm-table">
        <div class="tm-row tm-row--header">
          <div class="tm-cell tm-cell--name">Name</div>
          <div class="tm-cell tm-cell--color">Color</div>
          <div class="tm-cell tm-cell--type">Type</div>
          <div class="tm-cell tm-cell--perms">Permissions</div>
          <div class="tm-cell tm-cell--actions"></div>
        </div>
        @for (status of filteredStatuses; track status.statusId; let i = $index) {
          <div class="tm-row">
            <div class="tm-cell tm-cell--name">
              <input class="tm-input" type="text" [(ngModel)]="status.statusName" (blur)="updateStatus(status)" />
            </div>
            <div class="tm-cell tm-cell--color">
              <div class="color-palette-container">
                <div class="color-swatch-trigger"
                     [style.background-color]="status.color || '#4299E1'"
                     (click)="toggleColorPicker(status.statusId, $event)">
                </div>
                @if (activeColorPickerStatusId === status.statusId) {
                  <div class="color-palette-dropdown" [ngStyle]="colorPickerStyles">
                    <div class="color-palette-grid">
                      @for (color of colorPalette; track color) {
                        <div class="color-option"
                             [style.background-color]="color"
                             [class.selected]="status.color === color"
                             (click)="selectColor(status, color)">
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
            <div class="tm-cell tm-cell--type">
              <select class="tm-select" [(ngModel)]="status.statusType" (change)="updateStatus(status)">
                @for (type of statusTypes; track type.value) {
                  <option [value]="type.value">{{ type.label }}</option>
                }
              </select>
            </div>
            <div class="tm-cell tm-cell--perms">
              <select class="tm-select" [(ngModel)]="status.permissions" (change)="updateStatus(status)">
                @for (perm of permissionOptions; track perm.value) {
                  <option [value]="perm.value">{{ perm.label }}</option>
                }
              </select>
            </div>
            <div class="tm-cell tm-cell--actions">
              <button class="tm-icon-btn" type="button" (click)="moveStatusUp(status, i)" [disabled]="i === 0" title="Move up">
                <i class="ri-arrow-up-line"></i>
              </button>
              <button class="tm-icon-btn" type="button" (click)="moveStatusDown(status, i)" [disabled]="i === filteredStatuses.length - 1" title="Move down">
                <i class="ri-arrow-down-line"></i>
              </button>
              <button class="tm-icon-btn tm-icon-btn--danger" type="button" (click)="deleteStatus(status)" title="Delete">
                <i class="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- ===== Attributes Tab ===== -->
  @if (activeTab === 'attributes') {
    <div class="tm-panel">
      <div class="tm-panel-hdr">
        <p class="tm-panel-desc">Manage task attributes.</p>
        <button class="tm-btn-add" type="button" (click)="openCreateAttributeDialog()">
          <i class="ri-add-line"></i> Add Custom Attribute
        </button>
      </div>

      <div class="tm-table">
        <div class="tm-row tm-row--header">
          <div class="tm-cell tm-cell--attr-name">Name</div>
          <div class="tm-cell tm-cell--attr-type">Type</div>
          <div class="tm-cell tm-cell--display">Display</div>
          <div class="tm-cell tm-cell--actions">Order</div>
        </div>
        @for (attribute of filteredAttributes; track attribute.attributeId; let i = $index) {
          <div class="tm-row">
            <div class="tm-cell tm-cell--attr-name">
              <i [class]="'ri-' + getAttributeIconRi(attribute.attributeTypeName)"></i>
              <span class="tm-text">{{ attribute.attributeName }}</span>
            </div>
            <div class="tm-cell tm-cell--attr-type">
              <span class="tm-text">{{ attribute.attributeTypeName }}</span>
            </div>
            <div class="tm-cell tm-cell--display">
              @if (!attribute.isSystem || attribute.attributeName === 'Status' || attribute.attributeName === 'Category' || attribute.attributeName === 'Assignee') {
                <p-toggleSwitch [(ngModel)]="attribute.isDisplayed" (onChange)="toggleAttributeDisplay(attribute)"></p-toggleSwitch>
              }
            </div>
            <div class="tm-cell tm-cell--actions">
              <button class="tm-icon-btn" type="button" (click)="moveAttributeUp(attribute, i)" [disabled]="i === 0" title="Move up">
                <i class="ri-arrow-up-line"></i>
              </button>
              <button class="tm-icon-btn" type="button" (click)="moveAttributeDown(attribute, i)" [disabled]="i === filteredAttributes.length - 1" title="Move down">
                <i class="ri-arrow-down-line"></i>
              </button>
              <button class="tm-icon-btn" type="button" (click)="openEditAttributeDialog(attribute)" title="Edit">
                <i class="ri-pencil-line"></i>
              </button>
              @if (!attribute.isSystem) {
                <button class="tm-icon-btn tm-icon-btn--danger" type="button" (click)="deleteAttribute(attribute)" title="Delete">
                  <i class="ri-delete-bin-line"></i>
                </button>
              }
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- ===== Tags Tab ===== -->
  @if (activeTab === 'tags') {
    <div class="tm-panel">
      <div class="tm-panel-hdr">
        <p class="tm-panel-desc">Manage tags to categorize and organize your tasks.</p>
        <button class="tm-btn-add" type="button" (click)="openCreateTagDialog()">
          <i class="ri-add-line"></i> New Tag
        </button>
      </div>

      <div class="tm-table">
        <div class="tm-row tm-row--header">
          <div class="tm-cell tm-cell--tag-name">Name</div>
          <div class="tm-cell tm-cell--actions"></div>
        </div>
        @for (tag of filteredTags; track tag.tagId) {
          <div class="tm-row">
            <div class="tm-cell tm-cell--tag-name">
              <input class="tm-input" type="text" [(ngModel)]="tag.tagName" (blur)="updateTag(tag)" />
            </div>
            <div class="tm-cell tm-cell--actions">
              <button class="tm-icon-btn tm-icon-btn--danger" type="button" (click)="deleteTag(tag)" title="Delete">
                <i class="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  }

</div>

<!-- ===== Create Status Dialog ===== -->
<p-dialog header="New Status" [(visible)]="showCreateStatusDialog" [modal]="true" [style]="{width: '400px'}" styleClass="kansei-dialog">
  <div class="modal-form-body">
    <p class="modal-label">Status name <span class="req">*</span></p>
    <input type="text" class="tm-dialog-input" [(ngModel)]="newStatusName" placeholder="Enter status name" />
  </div>
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="modal-btn-cancel" type="button" (click)="showCreateStatusDialog.set(false)">Cancel</button>
      <button class="modal-btn-primary" type="button" (click)="createStatus()">Create</button>
    </div>
  </ng-template>
</p-dialog>

<!-- ===== Create/Edit Attribute Dialog ===== -->
<p-dialog [header]="isAttributeEdit() ? 'Edit Attribute' : 'Add Custom Attribute'" [(visible)]="showCreateAttributeDialog" [modal]="true" [style]="{width: '480px'}" styleClass="kansei-dialog">
  <div class="modal-form-body">
    @if (!isAttributeEdit()) {
      <div class="tm-field">
        <p class="modal-label">Attribute type <span class="req">*</span></p>
        <select class="tm-dialog-select" [(ngModel)]="selectedAttributeTypeId">
          <option [ngValue]="null" disabled>Select attribute type</option>
          @for (type of attributeTypes(); track type.attributeTypeId) {
            <option [ngValue]="type.attributeTypeId">{{ type.typeName }}</option>
          }
        </select>
      </div>
    }
    <div class="tm-field">
      <p class="modal-label">Attribute name <span class="req">*</span></p>
      <input type="text" class="tm-dialog-input" [(ngModel)]="newAttributeName" placeholder="Enter attribute name" />
    </div>
    @if (isDropdownType()) {
      <div class="tm-field">
        @if (isAttributeEdit() && existingOptions.length > 0) {
          <p class="modal-label">Existing Options</p>
          <div class="tm-options-list">
            @for (option of existingOptions; track $index) {
              <div class="tm-option-row">
                <input class="tm-dialog-input tm-input-flex" type="text" [(ngModel)]="option.valueText"
                  (blur)="updateExistingOption($index, option.valueText)" />
                <button class="tm-icon-btn tm-icon-btn--danger" type="button" (click)="removeExistingOption($index)">
                  <i class="ri-close-line"></i>
                </button>
              </div>
            }
          </div>
        }
        <p class="modal-label">{{ isAttributeEdit() ? 'Add New Option' : 'Options' }}</p>
        <div class="tm-option-row">
          <input class="tm-dialog-input tm-input-flex" type="text" [(ngModel)]="optionText" placeholder="Option text" />
          <button class="tm-btn-add" type="button" [disabled]="!optionText.trim()" (click)="addOptionText()">
            <i class="ri-add-line"></i> Add
          </button>
        </div>
        @if (attributeOptionValueList.length > 0) {
          <div class="tm-options-list">
            @for (option of attributeOptionValueList; track $index) {
              <div class="tm-option-row">
                <span class="tm-option-text">{{ option }}</span>
                <button class="tm-icon-btn tm-icon-btn--danger" type="button" (click)="removeOptionText($index)">
                  <i class="ri-close-line"></i>
                </button>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="modal-btn-cancel" type="button" (click)="showCreateAttributeDialog.set(false)">Cancel</button>
      <button class="modal-btn-primary" type="button" (click)="createAttribute()">
        {{ isAttributeEdit() ? 'Save' : 'Create' }}
      </button>
    </div>
  </ng-template>
</p-dialog>

<!-- ===== Create Tag Dialog ===== -->
<p-dialog header="New Tag" [(visible)]="showCreateTagDialog" [modal]="true" [style]="{width: '400px'}" styleClass="kansei-dialog">
  <div class="modal-form-body">
    <p class="modal-label">Tag name <span class="req">*</span></p>
    <input type="text" class="tm-dialog-input" [(ngModel)]="newTagName" placeholder="Enter tag name" />
  </div>
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button class="modal-btn-cancel" type="button" (click)="showCreateTagDialog.set(false)">Cancel</button>
      <button class="modal-btn-primary" type="button" (click)="createTag()">Create</button>
    </div>
  </ng-template>
</p-dialog>
